/**
 * @file apps/web/src/pages/ProjectDetailPage.tsx
 * @description Project command center — header, 5-phase lifecycle tracker,
 * a single "what to do next" banner, and a quick-stats row. All phase/next-
 * action data comes from GET /projects/:id/overview computed live from
 * Discovery, Documents, Approvals, UAT, and sign-off data.
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Building2, Users, ChevronRight, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { projectsApi } from '../lib/api.js';
import { Badge } from '../components/Badge.js';
import { PhaseTracker, type PhaseStatus } from '../components/PhaseTracker.js';
import { NextActionBanner, type NextAction } from '../components/NextActionBanner.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Project {
  id:            string;
  short_id:      string;
  name:          string;
  status:        string;
  description?:  string | null;
  go_live_date?: string | null;
  client_id:     string;
  erp_system?:   string | null;
  created_at:    string;
}

interface QuickStats {
  testScripts:      number;
  openDefects:      number;
  pendingApprovals: number;
  sessions:         number;
}

// ---------------------------------------------------------------------------
// Quick stat card
// ---------------------------------------------------------------------------

function QuickStat({ label, value, href }: { label: string; value: string | number; href: string }) {
  return (
    <Link to={href} className="card p-4 flex items-start justify-between group hover:ring-1 hover:ring-blue/30 transition-all">
      <div>
        <p className="section-label">{label}</p>
        <p className="font-display text-2xl font-bold text-ink mt-0.5">{value}</p>
      </div>
      <ChevronRight size={16} className="text-muted/40 group-hover:text-blue transition-colors mt-1" />
    </Link>
  );
}

// ---------------------------------------------------------------------------
// ProjectDetailPage
// ---------------------------------------------------------------------------

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { devHeaders } = useAuth();

  const [project, setProject]         = useState<Project | null>(null);
  const [phases, setPhases]           = useState<PhaseStatus[]>([]);
  const [nextAction, setNextAction]   = useState<NextAction | null>(null);
  const [quickStats, setQuickStats]   = useState<QuickStats | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    async function load() {
      try {
        const [proj, overview] = await Promise.all([
          projectsApi.get(projectId!, devHeaders) as Promise<Project>,
          projectsApi.overview(projectId!, devHeaders),
        ]);
        if (cancelled) return;
        setProject(proj);
        setPhases(overview.phases as PhaseStatus[]);
        setNextAction(overview.nextAction);
        setQuickStats(overview.quickStats);

        // The URL param may be the short_id (GET /projects/:id resolves
        // either) — every nested project route (/projects/:projectId/uat,
        // /discovery, etc.) is a sibling route, not wrapped by this page,
        // and queries its own data with that raw param assuming it's a
        // UUID. Rewriting the URL bar to the canonical UUID here means
        // every link built off it below (`base`) — and any link a user
        // clicks into a nested page from here — carries a real UUID, so
        // those sibling pages never see a short_id at all.
        if (proj.id !== projectId) {
          navigate(`/projects/${proj.id}`, { replace: true });
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load project');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // project.id, not the raw projectId param: when the URL carried a
  // short_id, nested routes (/uat, /approvals, ...) are separate top-level
  // routes that expect a real UUID — see the redirect above.
  const base = `/projects/${project?.id ?? projectId}`;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 rounded bg-gray" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[1,2,3,4,5].map((i) => <div key={i} className="card h-24 bg-gray/60" />)}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map((i) => <div key={i} className="card h-20 bg-gray/60" />)}
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="flex items-start gap-2 p-4 bg-danger/8 rounded-lg text-danger text-sm">
        <AlertCircle size={16} className="shrink-0 mt-0.5" />
        {error || 'Project not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Project header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-bold text-ink">{project.name}</h2>
            <Badge variant={project.status as never} label={project.status} />
          </div>
          {project.description && (
            <p className="text-sm text-muted mt-1 max-w-lg">{project.description}</p>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-4 text-xs font-mono text-muted">
        {project.erp_system && (
          <span className="flex items-center gap-1.5">
            <Building2 size={11} />
            {project.erp_system}
          </span>
        )}
        {project.go_live_date && (
          <span className="flex items-center gap-1.5">
            <Calendar size={11} />
            Go-live: {new Date(project.go_live_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Users size={11} />
          {project.short_id} · {project.id.slice(0, 8)}…
        </span>
      </div>

      {/* Next action */}
      {nextAction && <NextActionBanner action={nextAction} />}

      {/* Phase tracker */}
      <div>
        <h3 className="font-display text-sm font-semibold text-ink mb-3">Implementation Lifecycle</h3>
        <PhaseTracker phases={phases} />
      </div>

      {/* Quick stats */}
      <div>
        <h3 className="font-display text-sm font-semibold text-ink mb-3">Quick Stats</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickStat label="Test Scripts" value={quickStats?.testScripts ?? 0} href={`${base}/uat`} />
          <QuickStat label="Open Defects" value={quickStats?.openDefects ?? 0} href={`${base}/uat`} />
          <QuickStat label="Pending Approvals" value={quickStats?.pendingApprovals ?? 0} href={`${base}/approvals`} />
          <QuickStat label="UAT Sessions" value={quickStats?.sessions ?? 0} href={`${base}/uat`} />
        </div>
      </div>
    </div>
  );
}
